/**
 * Wave 4 — review-flag derivation.
 *
 * Pure functions that summarise uncertainty, contradictions, and soft-risk
 * signals already captured by the wizard. Consumed by the SummaryStep
 * "Things to double-check" panel and (selectively) by the HMRC form mapper
 * and report risk summary so they don't miss a positive GWR signal that lives
 * only on a property record.
 *
 * Adding new flags: append a `push(...)` block in `deriveReviewFlags` and a
 * matching ID. Don't change existing IDs — the SummaryStep panel uses them
 * as React keys.
 */

import { EstateProfile, EstateAssets, PropertyAsset, InheritsTo } from '../../types';
import { resolveResidenceForConsumers } from '../../server/services/rnrbResidenceFacts';

export type ReviewFlagSeverity = 'high' | 'medium' | 'low';

export interface ReviewFlag {
    id: string;
    title: string;
    message: string;
    severity: ReviewFlagSeverity;
    /** Optional wizard step id the user can jump to in order to fix this. */
    stepId?: string;
    /** Optional human-readable list of affected items (e.g. property labels). */
    affectedLabels?: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * True when any property explicitly answered GWR='yes' OR the estate-level
 * trust_screening flag is set. Used as the canonical "is GWR in play?" check
 * everywhere downstream.
 *
 * NOTE — we deliberately do NOT clear the estate-level flag from PropertyStep
 * when a property's answer flips back to 'no'. Other (non-property) GWR
 * sources can also set the estate-level flag from ComplexityStep. This
 * derived helper is the single point that combines them, so the underlying
 * stored flag stays as-is and the truth is computed on read.
 */
export function deriveHasGiftWithReservation(
    profile: EstateProfile | null | undefined,
    assets: EstateAssets | null | undefined,
): boolean {
    if (profile?.iht_details?.trust_screening?.has_gift_with_reservation === true) return true;
    if (assets?.properties?.some(p => p.gift_with_reservation === 'yes')) return true;
    return false;
}

/** Return the property records (if any) that explicitly answered GWR='yes'. */
export function getGwrProperties(assets: EstateAssets | null | undefined): PropertyAsset[] {
    return (assets?.properties ?? []).filter(p => p.gift_with_reservation === 'yes');
}

/** Display label for a property: prefer label, fall back to address, then index. */
export function propertyDisplayLabel(p: PropertyAsset, idx: number): string {
    const label = p.label?.trim();
    if (label) return label;
    const address = p.address?.trim();
    if (address) return address;
    return `Property ${idx + 1}`;
}

/**
 * True when this property's deceased share appears to pass to direct
 * descendants. Reads the same fields the existing RNRB calc uses:
 *   - joint_tenants: joint_owner_relationship === 'direct_descendant'
 *   - sole / TIC single mode: deceased_share_passes_to === 'direct_descendants'
 *   - sole / TIC split mode: any beneficiary_allocations row of type
 *     'direct_descendant' with > 0%
 */
export function propertyPassesToDescendants(p: PropertyAsset): boolean {
    if (p.ownership_type === 'joint_tenants') {
        return p.joint_owner_relationship === 'direct_descendant';
    }
    if (p.beneficiary_allocations && p.beneficiary_allocations.length > 0) {
        return p.beneficiary_allocations.some(
            a => a.type === 'direct_descendant' && a.percentage > 0
        );
    }
    const passes: InheritsTo | null = p.deceased_share_passes_to;
    return passes === 'direct_descendants';
}

// ---------------------------------------------------------------------------
// Main derivation
// ---------------------------------------------------------------------------

export function deriveReviewFlags(
    profile: EstateProfile,
    assets: EstateAssets,
): ReviewFlag[] {
    const flags: ReviewFlag[] = [];

    const ihtDetails = profile.iht_details ?? {};
    const trustScreening = ihtDetails.trust_screening ?? {};
    const predeceased = ihtDetails.predeceased_spouse ?? {};
    const family = profile.family ?? {};
    const deceased = profile.deceased ?? {};
    const properties = assets.properties ?? [];

    // ── A. Trust uncertainty ───────────────────────────────────────────────
    if (assets.trusts_or_life_interest?.unsure === true &&
        assets.trusts_or_life_interest?.exists !== true) {
        flags.push({
            id: 'trust_uncertain',
            title: 'Trust involvement uncertain',
            message:
                'Trust involvement is uncertain. Check whether any trusts or life interests affect ' +
                'the estate and whether IHT400/IHT418 may be required.',
            severity: 'medium',
            stepId: 'discovery',
        });
    }

    // ── B. Origin outside UK / domicile review ─────────────────────────────
    if (deceased.origin_outside_uk === 'yes' || deceased.origin_outside_uk === 'unsure') {
        flags.push({
            id: 'origin_outside_uk',
            title: 'Domicile / long-residence may need review',
            message:
                'Domicile or long-residence status may need review because the deceased was ' +
                'originally from outside the UK or this is uncertain.',
            severity: 'medium',
            stepId: 'complexity',
        });
    }

    // ── C. Spouse exemption conflict ───────────────────────────────────────
    if (
        family.assets_passed_to_spouse === 'yes' &&
        (family.current_spouse_alive === 'no' || family.current_spouse_alive === 'unsure')
    ) {
        flags.push({
            id: 'spouse_exemption_conflict',
            title: 'Spouse exemption assumption conflicts with surviving-spouse status',
            message:
                'Assets are marked as passing to a spouse or civil partner, but the surviving-spouse ' +
                'status is unclear or marked as no. The spouse exemption may not apply unless there ' +
                'was a surviving spouse or civil partner at death.',
            severity: 'high',
            stepId: 'basics',
        });
    }

    // ── D. Residence state ─────────────────────────────────────────────────
    // From the shared Stage-1 resolution, never is_main_residence: a v2 estate's explicit answers and nomination; an
    // unstamped estate's legacy policy B (a false/null marker is unknown, never "no main residence").
    const residence = resolveResidenceForConsumers(properties, ihtDetails);
    const residenceLabels = (ps: PropertyAsset[]) => ps.map(p => propertyDisplayLabel(p, properties.indexOf(p)));
    if (properties.length > 0 && residence.noCurrentRpi) {
        flags.push({
            id: 'no_main_residence',
            title: 'No property recorded as a home they lived in',
            message:
                'No property in the estate is recorded as one they lived in as their home. The Residence Nil Rate ' +
                'Band can then only come from a former home they sold or gave away (downsizing).',
            severity: 'medium',
            stepId: 'property',
        });
    } else if (residence.nominationRequired) {
        flags.push({
            id: 'qri_nomination_required',
            title: 'Residence for the RNRB not yet nominated',
            message:
                'More than one property may count as their residence. Only one can be used for the Residence Nil Rate ' +
                'Band, and the executors or administrators choose which. Until it is nominated the RNRB is withheld.',
            severity: 'medium',
            stepId: 'property',
            affectedLabels: residenceLabels(residence.candidates),
        });
    } else if (residence.residenceHistoryUnknown) {
        const unknown = residence.candidates.filter(p => !(residence.mode === 'v2' && p.has_been_residence === 'yes'));
        flags.push({
            id: 'residence_history_unknown',
            title: 'Whether they lived in a property is not yet known',
            message:
                'For at least one property it is not yet known whether they lived in it as their home. Until this is ' +
                'answered, the Residence Nil Rate Band on a home cannot be worked out.',
            severity: 'medium',
            stepId: 'property',
            affectedLabels: residenceLabels(unknown),
        });
    }

    // ── E. Basics vs Property RNRB mismatch ────────────────────────────────
    // "yes" is contradicted only when no property that is or may still be the residence passes to descendants; "no"
    // only by a resolved (or legacy provisional) residence that does.
    const basicsMainToDescendants = ihtDetails.main_residence_to_descendants;
    const mainGoesToDescendants = !!residence.qri && propertyPassesToDescendants(residence.qri);
    const anyCandidateToDescendants = residence.candidates.some(propertyPassesToDescendants);

    if (basicsMainToDescendants === 'yes' && properties.length > 0 && !anyCandidateToDescendants) {
        flags.push({
            id: 'rnrb_mismatch_yes_no',
            title: 'Basics says main home goes to descendants, but property details do not confirm it',
            message:
                'The Basics section says the main home is expected to pass to direct descendants, ' +
                'but the property details do not yet confirm this. Check the main residence and ' +
                'inheritance destination.',
            severity: 'medium',
            stepId: 'property',
        });
    } else if (basicsMainToDescendants === 'no' && mainGoesToDescendants) {
        flags.push({
            id: 'rnrb_mismatch_no_yes',
            title: 'Property details suggest descendants inherit the main home, but Basics says no',
            message:
                'The property details suggest the main residence may pass to direct descendants, ' +
                'but the Basics section says it does not. Check which answer is correct.',
            severity: 'low',
            stepId: 'basics',
        });
    }

    // ── F. Unknown property ownership ──────────────────────────────────────
    const unknownOwnershipProps = properties.filter(p =>
        p.ownership_type === 'unknown' || (p.ownership_type as unknown as string) === 'joint'
    );
    if (unknownOwnershipProps.length > 0) {
        flags.push({
            id: 'unknown_ownership',
            title: 'Joint ownership type unclear',
            message:
                'One or more properties are jointly owned, but the ownership type is unclear. ' +
                'Joint tenants and tenants in common can be treated differently for probate and ' +
                'inheritance purposes.',
            severity: 'medium',
            stepId: 'property',
            affectedLabels: unknownOwnershipProps.map((p, i) =>
                propertyDisplayLabel(p, properties.indexOf(p))
            ),
        });
    }

    // ── G. Pension uncertainty ─────────────────────────────────────────────
    const pensions = assets.pensions ?? [];
    const uncertainPensions = pensions.filter(p => p.in_estate === null);
    if (uncertainPensions.length > 0) {
        flags.push({
            id: 'pension_uncertain',
            title: 'Pension treatment uncertain',
            message:
                'Pension treatment is uncertain. Pensions paid directly to beneficiaries are usually ' +
                'outside the estate, but pensions paid into the estate may affect the calculation.',
            severity: 'medium',
            stepId: 'pensions',
            affectedLabels: uncertainPensions.map(p => p.label?.trim() || p.provider?.trim() || 'Pension'),
        });
    }

    // ── H. Previous spouse uncertainty ─────────────────────────────────────
    if (predeceased.exists_uncertain === true) {
        flags.push({
            id: 'predeceased_uncertain',
            title: 'Previous spouse status uncertain',
            message:
                'It is unclear whether a previous spouse or civil partner died before the deceased. ' +
                'This may affect transferable Nil Rate Band or Residence Nil Rate Band allowances.',
            severity: 'medium',
            stepId: 'basics',
        });
    }

    // ── I. Gift with reservation (uses derived helper) ─────────────────────
    if (deriveHasGiftWithReservation(profile, assets)) {
        const gwrProps = getGwrProperties(assets);
        const affectedLabels = gwrProps.map(p =>
            propertyDisplayLabel(p, properties.indexOf(p))
        );
        flags.push({
            id: 'gift_with_reservation',
            title: 'Possible gift with reservation of benefit',
            message:
                'There may be a gift with reservation of benefit. For example, the deceased may ' +
                'have given away an asset but continued to benefit from it. This may need ' +
                'specialist review.',
            severity: 'high',
            stepId: gwrProps.length > 0 ? 'property' : 'complexity',
            affectedLabels: affectedLabels.length > 0 ? affectedLabels : undefined,
        });
    }

    return flags;
}

// ---------------------------------------------------------------------------
// TODO (deferred per Wave 4 instruction):
//   Replace the single estate-level `trust_screening.has_gift_with_reservation`
//   storage with source-aware fields so the read path can attribute origin:
//     - property_gwr_detected      (derived from PropertyStep)
//     - complexity_gwr_reported    (set in ComplexityStep)
//     - other_asset_gwr_detected   (future BPR / large-asset signals)
//   For now, `deriveHasGiftWithReservation` masks the storage shape and
//   PropertyStep keeps a write-through-only contract — never clearing.
// ---------------------------------------------------------------------------
